import React, { useContext, useEffect, useState } from 'react';
import { Trash2, Loader, Calculator } from 'lucide-react';
import { AuthContext } from '../../../../context/AuthContext';
import { addDrillToAgent } from '../../../../services/api';

const AgentDrillingForm = ({selectedAgentForAction, showDrillingForm, setShowDrillingForm}) => {
    // Get vehicles data and functions from context
    const {
        getAllVehicles,
        allVehicles,
        allVehiclesLoading,
        allVehiclesError,
        getAllAgents,
        allAgents,
        allAgentsLoading,
        allAgentsError,
    } = useContext(AuthContext);


    const [formLoading, setFormLoading] = useState(false);
    const [error, setError] = useState(null);

    const [newDrillingData, setNewDrillingData] = useState({
        date: new Date().toISOString().split('T')[0],
        vehicleName: '',
        typeOfCustomer: 'agent',
        name: '',
        address: '',
        mobileNumber: '',
        drillFeet: '',
        drillRate: '',
        totalDrillAmount: '',  //totalDrillAmount = drillFeet*drillRate
        additional: [{ name: '', quantity: '', rate: '', total: '' }],
        totalBillAmount: '', //totalBillAmount = sumof(additional.total) +totalDrillAmount
    });

    useEffect(() => {
        if (!allVehicles || allVehicles.length === 0) {
            getAllVehicles();
        }
    }, []);

    // Calculate total drill amount whenever drillFeet or drillRate changes
    useEffect(() => {
        const feet = parseFloat(newDrillingData.drillFeet) || 0;
        const rate = parseFloat(newDrillingData.drillRate) || 0;
        const totalDrillAmount = feet * rate;

        setNewDrillingData(prev => ({
            ...prev,
            totalDrillAmount: totalDrillAmount.toFixed(2)
        }));
    }, [newDrillingData.drillFeet, newDrillingData.drillRate]);

    // Calculate total bill amount whenever totalDrillAmount or additional items change
    useEffect(() => {
        const drillAmount = parseFloat(newDrillingData.totalDrillAmount) || 0;
        const additionalTotal = newDrillingData.additional.reduce((sum, item) => {
            return sum + (parseFloat(item.total) || 0);
        }, 0);
        const totalBillAmount = drillAmount + additionalTotal;

        setNewDrillingData(prev => ({
            ...prev,
            totalBillAmount: totalBillAmount.toFixed(2)
        }));
    }, [newDrillingData.totalDrillAmount, newDrillingData.additional]);

    // Handle additional items
    const addAdditionalItem = () => {
        setNewDrillingData({
            ...newDrillingData,
            additional: [...newDrillingData.additional, { name: '', quantity: '', rate: '', total: '' }]
        });
    };

    const removeAdditionalItem = (index) => {
        if (newDrillingData.additional.length > 1) {
            const updatedAdditional = newDrillingData.additional.filter((_, i) => i !== index);
            setNewDrillingData({
                ...newDrillingData,
                additional: updatedAdditional
            });
        }
    };

    const updateAdditionalItem = (index, field, value) => {
        const updatedAdditional = newDrillingData.additional.map((item, i) => {
            if (i === index) {
                const updatedItem = { ...item, [field]: value };

                // Auto-calculate total when quantity or rate changes
                if (field === 'quantity' || field === 'rate') {
                    const quantity = parseFloat(field === 'quantity' ? value : updatedItem.quantity) || 0;
                    const rate = parseFloat(field === 'rate' ? value : updatedItem.rate) || 0;
                    updatedItem.total = (quantity * rate).toFixed(2);
                }

                return updatedItem;
            }
            return item;
        });

        setNewDrillingData({
            ...newDrillingData,
            additional: updatedAdditional
        });
    };

    // Form validation
    const validateForm = () => {
        if (!newDrillingData.date) {
            setError('Date is required');
            return false;
        }
        if (!newDrillingData.vehicleName) {
            setError('Vehicle selection is required');
            return false;
        }
        if (newDrillingData.mobileNumber && !/^\d{10}$/.test(newDrillingData.mobileNumber.replace(/\D/g, ''))) {
            setError('Please enter a valid 10-digit mobile number');
            return false;
        }
        if (newDrillingData.drillFeet && isNaN(parseFloat(newDrillingData.drillFeet))) {
            setError('Drill feet must be a valid number');
            return false;
        }
        if (newDrillingData.drillRate && isNaN(parseFloat(newDrillingData.drillRate))) {
            setError('Drill rate must be a valid number');
            return false;
        }
        return true;
    };

    // Handle form submission
    const handleSubmitDrillingData = async () => {
        setError(null);

        if (!validateForm()) {
            return;
        }

        setFormLoading(true);

        try {
            console.log(newDrillingData, "newDrillingData-newDrillingData")
            // Simulate API call
            const response = await addDrillToAgent(selectedAgentForAction._id, newDrillingData);

            console.log('Drilling project data:', {
                ...newDrillingData,
                agentId: selectedAgentForAction?.id,
                agentName: selectedAgentForAction?.name
            });

            // Reset form and close modal
            resetForm();
            alert('Drilling project created successfully!');

        } catch (err) {
            setError('Failed to create drilling project. Please try again.');
        } finally {
            setFormLoading(false);
        }
    };

    // Reset form function
    const resetForm = () => {
        setShowDrillingForm(false);
        setNewDrillingData({
            date: new Date().toISOString().split('T')[0],
            vehicleName: '',
            typeOfCustomer: 'agent',
            name: '',
            address: '',
            mobileNumber: '',
            drillFeet: '',
            drillRate: '',
            totalDrillAmount: '',
            additional: [{ name: '', quantity: '', rate: '', total: '' }],
            totalBillAmount: '',
        });
        setError(null);
    };

    if (!showDrillingForm) {
        return (
            <div className="p-8 text-center">
                <button
                    onClick={() => {
                        setShowDrillingForm(true);
                    }}
                    className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                    Open Drilling Form
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">
                                Add Drilling Project for {selectedAgentForAction?.name}
                            </h2>
                            <button
                                onClick={resetForm}
                                className="text-gray-400 hover:text-gray-600 text-2xl"
                                disabled={formLoading}
                            >
                                ✕
                            </button>
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                                {error}
                            </div>
                        )}

                        <div className="space-y-6">
                            {/* Basic Information */}
                            <div className="border-b pb-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                                        <input
                                            type="date"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={newDrillingData.date}
                                            onChange={(e) => setNewDrillingData({ ...newDrillingData, date: e.target.value })}
                                            required
                                            disabled={formLoading}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Name *</label>
                                        <select
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={newDrillingData.vehicleName}
                                            onChange={(e) => setNewDrillingData({ ...newDrillingData, vehicleName: e.target.value })}
                                            required
                                            disabled={formLoading || allVehiclesLoading}
                                        >
                                            <option value="">
                                                {allVehiclesLoading ? 'Loading vehicles...' : 'Select a vehicle'}
                                            </option>
                                            {allVehicles && allVehicles.length > 0 && allVehicles.map((vehicle) => (
                                                <option key={vehicle._id} value={vehicle._id}>
                                                    {vehicle.vehicleName} ({vehicle.vehicleNumber})
                                                </option>
                                            ))}
                                        </select>
                                        {allVehiclesError && (
                                            <p className="text-red-500 text-xs mt-1">Error loading vehicles. Please try again.</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Customer Information */}
                            <div className="border-b pb-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Customer Information</h3>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={newDrillingData.name}
                                            onChange={(e) => setNewDrillingData({ ...newDrillingData, name: e.target.value })}
                                            placeholder="Enter customer name"
                                            disabled={formLoading}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                                        <input
                                            type="tel"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={newDrillingData.mobileNumber}
                                            onChange={(e) => setNewDrillingData({ ...newDrillingData, mobileNumber: e.target.value })}
                                            placeholder="Enter 10-digit mobile number"
                                            disabled={formLoading}
                                        />
                                    </div>

                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Address</label>
                                    <textarea
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        rows="2"
                                        value={newDrillingData.address}
                                        onChange={(e) => setNewDrillingData({ ...newDrillingData, address: e.target.value })}
                                        placeholder="Enter customer address"
                                        disabled={formLoading}
                                    />
                                </div>

                                <div>

                                </div>
                            </div>

                            {/* Drilling Information */}
                            <div className="border-b pb-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                    <Calculator className="mr-2" size={20} />
                                    Drilling Information
                                </h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Drill Feet</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={newDrillingData.drillFeet}
                                            onChange={(e) => setNewDrillingData({ ...newDrillingData, drillFeet: e.target.value })}
                                            placeholder="Enter depth in feet"
                                            disabled={formLoading}
                                            min="0"
                                            step="0.1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Drill Rate (₹/ft)</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={newDrillingData.drillRate}
                                            onChange={(e) => setNewDrillingData({ ...newDrillingData, drillRate: e.target.value })}
                                            placeholder="Enter rate per foot"
                                            disabled={formLoading}
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Total Drill Amount (₹)</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-semibold"
                                            value={newDrillingData.totalDrillAmount}
                                            placeholder="Auto-calculated"
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Additional Items */}
                            <div className="border-b pb-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-800">Additional Items</h3>
                                    <button
                                        onClick={addAdditionalItem}
                                        className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 transition-colors disabled:opacity-50"
                                        disabled={formLoading}
                                    >
                                        Add Item
                                    </button>
                                </div>

                                <div className="space-y-3">
                                    {newDrillingData.additional.map((item, index) => (
                                        <div key={index} className="bg-gray-50 p-4 rounded-lg">
                                            <div className="grid grid-cols-12 gap-3 items-end">
                                                <div className="col-span-4">
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">Item Name</label>
                                                    <input
                                                        type="text"
                                                        className="w-full px-2 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                        value={item.name}
                                                        onChange={(e) => updateAdditionalItem(index, 'name', e.target.value)}
                                                        placeholder="Item name"
                                                        disabled={formLoading}
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">Quantity</label>
                                                    <input
                                                        type="number"
                                                        className="w-full px-2 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                        value={item.quantity}
                                                        onChange={(e) => updateAdditionalItem(index, 'quantity', e.target.value)}
                                                        placeholder="Qty"
                                                        disabled={formLoading}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">Rate (₹)</label>
                                                    <input
                                                        type="number"
                                                        className="w-full px-2 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                        value={item.rate}
                                                        onChange={(e) => updateAdditionalItem(index, 'rate', e.target.value)}
                                                        placeholder="Rate"
                                                        disabled={formLoading}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                </div>
                                                <div className="col-span-3">
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">Total (₹)</label>
                                                    <input
                                                        type="text"
                                                        className="w-full px-2 py-2 border border-gray-300 rounded text-sm bg-gray-100 text-gray-700 font-semibold"
                                                        value={item.total}
                                                        placeholder="Auto-calculated"
                                                        disabled={true}
                                                    />
                                                </div>
                                                <div className="col-span-1">
                                                    {newDrillingData.additional.length > 1 && (
                                                        <button
                                                            onClick={() => removeAdditionalItem(index)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                                            disabled={formLoading}
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Total Bill Amount */}
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-lg font-bold text-blue-900">Total Bill Amount</h3>
                                        <p className="text-sm text-blue-700">Drilling Amount + Additional Items</p>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-2xl font-bold text-blue-900">
                                            ₹{newDrillingData.totalBillAmount || '0.00'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex space-x-3 mt-6">
                            <button
                                onClick={resetForm}
                                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                                disabled={formLoading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmitDrillingData}
                                className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center disabled:opacity-50"
                                disabled={!newDrillingData.date || !newDrillingData.vehicleName || formLoading}
                            >
                                {formLoading ? (
                                    <Loader size={20} className="animate-spin" />
                                ) : (
                                    'Create Project'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AgentDrillingForm;